import React, { useMemo } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../../utils/helpers';

export const CashFlowChart = ({ transactions, accounts, startDate, endDate }) => {

    const cashFlowData = useMemo(() => {
        if (!Array.isArray(transactions) || !Array.isArray(accounts) || accounts.length === 0 || !startDate || !endDate) {
            return [];
        }

        const transactionsByDate = transactions.reduce((acc, t) => {
            const dateStr = t.date.split('T')[0];
            if (!acc[dateStr]) {
                acc[dateStr] = { cashIn: 0, cashOut: 0 };
            }
            if (t.type === 'Thu') {
                acc[dateStr].cashIn += t.amount;
            } else {
                acc[dateStr].cashOut += t.amount;
            }
            return acc;
        }, {});

        const currentTotalBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
        
        const totalTransactionNet = transactions.reduce((sum, t) => {
            return sum + (t.type === 'Thu' ? t.amount : -t.amount);
        }, 0);

        const initialSystemBalance = currentTotalBalance - totalTransactionNet;
        
        const chartData = [];
        const periodStartDate = new Date(startDate);
        const periodEndDate = new Date(endDate);
        
        let balanceBeforePeriod = initialSystemBalance;
        
        const allTransactionDates = Object.keys(transactionsByDate).sort();

        for (const dateStr of allTransactionDates) {
            const transactionDate = new Date(dateStr);
            transactionDate.setHours(12, 0, 0, 0); // Normalize time to avoid timezone issues
            if (transactionDate < periodStartDate) {
                const dailyTrans = transactionsByDate[dateStr];
                balanceBeforePeriod += dailyTrans.cashIn - dailyTrans.cashOut;
            }
        }

        let dateIterator = new Date(startDate);
        let runningBalance = balanceBeforePeriod;

        while (dateIterator <= periodEndDate) {
            const dateStr = dateIterator.toISOString().split('T')[0];
            const [year, month, day] = dateStr.split('-');
            const dailyTrans = transactionsByDate[dateStr] || { cashIn: 0, cashOut: 0 };

            runningBalance += (dailyTrans.cashIn - dailyTrans.cashOut);
            
            chartData.push({
                name: `${day}/${month}`,
                'Tiền vào': dailyTrans.cashIn,
                'Tiền ra': dailyTrans.cashOut,
                'Số dư cuối ngày': runningBalance,
            });
            
            dateIterator.setDate(dateIterator.getDate() + 1);
        }
        
        return chartData;

    }, [transactions, accounts, startDate, endDate]);

    if (cashFlowData.length === 0) {
        return (
            <div className="bg-white p-5 rounded-lg shadow-md">
                 <h3 className="font-bold text-lg text-gray-800 mb-4">Biểu đồ Dòng tiền & Số dư</h3>
                 <div className="flex items-center justify-center h-80">
                    <p className="text-center p-10 text-gray-500">Không có dữ liệu giao dịch trong khoảng thời gian đã chọn.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-5 rounded-lg shadow-md">
            <h3 className="font-bold text-lg text-gray-800 mb-4">Biểu đồ Dòng tiền & Số dư</h3>
            <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={cashFlowData}
                        margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" />
                        <YAxis yAxisId="left" tickFormatter={(value) => new Intl.NumberFormat('vi-VN').format(value)} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => new Intl.NumberFormat('vi-VN').format(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="Tiền vào" fill="#22c55e" name="Tiền vào" />
                        <Bar yAxisId="left" dataKey="Tiền ra" fill="#ef4444" name="Tiền ra" />
                        <Line yAxisId="right" type="monotone" dataKey="Số dư cuối ngày" stroke="#3b82f6" strokeWidth={2} name="Số dư cuối ngày" />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};